import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { formatCurrency, buildTeamShareUrl } from 'utils'
import { useModalBehavior, usePromotionCopy, useDiscountPreviews } from 'hooks'
import PromotionLinkModal from './PromotionLinkModal'
import { trackEvent } from 'utils/analytics'
import VideoOrImage from '../shared/VideoOrImage'
import CourseCategoryModal from './CourseCategoryModal'
import PackageSelector from '../shared/PackageSelector'
import CoursePreviewCard from '../shared/CoursePreviewCard'

const INITIAL_DISPLAY_COUNT = 4

export default function TeamCoursesSection({ courses, courseCategories, colors, onPurchase, onClaimTrial, trialUsed, translations, teamSlug, initialCourseId, initialCategoryId, currentUser, teamId, onConsult, consultationEnabled }) {
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedPackages, setSelectedPackages] = useState({})
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    setSelectedPackages({})
  }, [courses])

  // Auto-open course detail from ?course= deep link
  useEffect(() => {
    if (!initialCourseId || !courses?.length) return
    const idx = courses.findIndex(c => c.id === initialCourseId)
    if (idx === -1) return
    if (idx >= INITIAL_DISPLAY_COUNT) setExpanded(true)
    openCourseDetail(courses[idx])
  }, [initialCourseId])

  // Auto-open category modal from ?category= deep link
  useEffect(() => {
    if (!initialCategoryId || !courseCategories?.length) return
    const cat = courseCategories.find(c => c.id === initialCategoryId)
    if (!cat || !cat.courseIds?.length) return
    setExpanded(true)
    setSelectedCategory(cat)
  }, [initialCategoryId])

  const openCourseDetail = (course) => {
    trackEvent('course_detail_view', { course_name: course.name, team_slug: teamSlug })
    setSelectedCourse(course)
  }

  const closeCourseDetail = useCallback(() => {
    setSelectedCourse(null)
  }, [])

  useModalBehavior(!!selectedCourse, closeCourseDetail)

  const handleSelectPackage = (courseId, packageId, courseName, lessonCount) => {
    trackEvent('package_select', {
      course_name: courseName,
      lesson_count: lessonCount,
      team_slug: teamSlug
    })
    setSelectedPackages(prev => ({
      ...prev,
      [courseId]: packageId
    }))
  }

  const handleAction = (course) => {
    const selectedPkgId = selectedPackages[course.id]
    const selectedPkg = course.packages?.find(p => p.id === selectedPkgId)
    if (!selectedPkg) return

    trackEvent('purchase_click', {
      course_name: course.name,
      lesson_count: selectedPkg.lessonCount,
      price: selectedPkg.totalPrice,
      is_trial: selectedPkg.isTrial || false,
      source: 'courses_section',
      team_slug: teamSlug
    })

    if (selectedPkg.isTrial) {
      if (selectedPkg.totalPrice === 0) {
        onClaimTrial?.(course)
      } else {
        onPurchase(course, selectedPkg)
      }
    } else {
      onPurchase(course, selectedPkg)
    }
  }

  const closeCategoryModal = useCallback(() => {
    setSelectedCategory(null)
  }, [])

  useModalBehavior(!!selectedCategory, closeCategoryModal)

  // Build mixed items: categories + uncategorized courses
  const mixedItems = useMemo(() => {
    const activeCategories = (courseCategories || []).filter(cat => cat.courseIds?.length > 0)
    const categorizedCourseIds = new Set(activeCategories.flatMap(cat => cat.courseIds))

    const uncategorizedCourses = (courses || []).filter(c => !categorizedCourseIds.has(c.id))

    // Build items with type and popularity
    // Mark category as popular if it contains the most popular course
    const popularCourseIds = new Set((courses || []).filter(c => c.popular).map(c => c.id))
    const categoryItems = activeCategories.map(cat => ({
      ...cat,
      type: 'category',
      popular: (cat.courseIds || []).some(id => popularCourseIds.has(id)),
    }))

    const courseItems = uncategorizedCourses.map((course, idx) => ({
      ...course,
      type: 'course',
      popularity: course.popular ? 999 : (courses.length - idx),
    }))

    // Sort by popularity desc, then position asc
    return [...categoryItems, ...courseItems].sort((a, b) => {
      const popDiff = (b.popularity || 0) - (a.popularity || 0)
      if (popDiff !== 0) return popDiff
      return (a.position || 999) - (b.position || 999)
    })
  }, [courses, courseCategories])

  if ((!courses || courses.length === 0) && (!courseCategories || courseCategories.length === 0)) return null

  return (
    <section className="py-8 border-t border-gray-200">
      <div className="mb-6">
        <h2 className="text-[22px] font-semibold" style={{ color: '#222222' }}>{translations?.title || 'Courses'}</h2>
        <p className="text-sm mt-1" style={{ color: '#717171' }}>{translations?.subtitle || 'Choose the package that works best for you'}</p>
      </div>

      {/* Mixed Preview Cards (categories + uncategorized courses) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {(expanded ? mixedItems : mixedItems.slice(0, INITIAL_DISPLAY_COUNT)).map((item) => (
          <CoursePreviewCard
            key={item.type === 'category' ? `cat-${item.id}` : `course-${item.id}`}
            imageUrl={item.imageUrl}
            videoThumbnailUrl={item.videoThumbnailUrl}
            name={item.name}
            targetAudience={item.targetAudience}
            popular={item.popular}
            popularLabel={translations?.popularBadge}
            buttonLabel={item.type === 'category' ? (translations?.categoryLearnMore || 'Learn More') : (translations?.learnMore || 'Learn More')}
            onClick={() => item.type === 'category' ? setSelectedCategory(item) : openCourseDetail(item)}
            colors={colors}
            studentsCount={item.studentsCount}
            lessonsCount={item.lessonsCount}
            studentsLabel={translations?.studentsUnit}
            lessonsLabel={translations?.lessonsCompleted}
          />
        ))}
      </div>

      {/* See More Button */}
      {!expanded && mixedItems.length > INITIAL_DISPLAY_COUNT && (
        <div className="mt-6 text-center">
          <button
            onClick={() => setExpanded(true)}
            className="px-6 py-2.5 rounded-xl font-semibold transition-all cursor-pointer border-2 hover:opacity-80"
            style={{ color: colors.primary, borderColor: colors.primary }}
          >
            {translations?.seeMore || 'See more'}
          </button>
        </div>
      )}

      {/* Guidance CTA */}
      {onConsult && (
        <div className="mt-8 rounded-xl p-6 text-center" style={{ backgroundColor: '#E6F0FF' }}>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#222' }}>
            {translations?.guidanceTitle || '不知道從哪開始？'}
          </h3>
          <p className="text-sm mb-4" style={{ color: '#717171' }}>
            {translations?.guidanceSubtitle || '填個簡單表單，老師幫你推薦最適合的課程'}
          </p>
          <button
            onClick={() => onConsult({ source: 'guidance' })}
            className="px-6 py-2.5 rounded-xl font-semibold text-white shadow-md hover:shadow-lg transition-all cursor-pointer"
            style={{ background: colors.primaryGradient || 'linear-gradient(to right, #3366FF, #5588FF)' }}
          >
            {translations?.guidanceButton || '先跟老師聊聊'}
          </button>
        </div>
      )}

      {/* Course Detail Modal */}
      {selectedCourse && (
        <CourseDetailModal
          course={selectedCourse}
          colors={colors}
          translations={translations}
          trialUsed={trialUsed}
          selectedPackages={selectedPackages}
          onSelectPackage={handleSelectPackage}
          onPurchase={onPurchase}
          onClaimTrial={onClaimTrial}
          onClose={closeCourseDetail}
          teamSlug={teamSlug}
          currentUser={currentUser}
          teamId={teamId}
          onConsult={onConsult}
          consultationEnabled={consultationEnabled}
        />
      )}

      {/* Category Modal */}
      {selectedCategory && (
        <CourseCategoryModal
          category={selectedCategory}
          courses={courses}
          colors={colors}
          translations={translations}
          trialUsed={trialUsed}
          onPurchase={onPurchase}
          onClaimTrial={onClaimTrial}
          onClose={closeCategoryModal}
          teamSlug={teamSlug}
          currentUser={currentUser}
          teamId={teamId}
          onConsult={onConsult}
          consultationEnabled={consultationEnabled}
        />
      )}
    </section>
  )
}

function CourseDetailModal({ course, colors, translations, trialUsed, selectedPackages, onSelectPackage, onPurchase, onClaimTrial, onClose, teamSlug, currentUser, teamId, onConsult, consultationEnabled }) {
  const { handleCopyClick, modalState, closeModal: closePromoModal, isCopied, inviteCode } = usePromotionCopy({ currentUser, teamId })
  const courseShareUrl = buildTeamShareUrl(teamSlug, { course: course.id }, inviteCode)
  const trialPkg = !trialUsed ? course.packages?.find(p => p.isTrial) : null
  const regularPkgs = course.packages?.filter(p => !p.isTrial) || []
  const visiblePkgs = [...(trialPkg ? [trialPkg] : []), ...regularPkgs]
  const discountPreviews = useDiscountPreviews(teamId, !!currentUser?.id)
  const selectedPkgId = selectedPackages[course.id]
  const selectedPkg = visiblePkgs.find(p => p.id === selectedPkgId)
  const isTrialSelected = selectedPkg?.isTrial

  // Gift purchase state
  const isTeamMember = !!(currentUser?.teamRole)
  const [giftMode, setGiftMode] = useState(false)
  const [teamStudents, setTeamStudents] = useState([])
  const [selectedRecipientId, setSelectedRecipientId] = useState(null)
  const [loadingStudents, setLoadingStudents] = useState(false)

  useEffect(() => {
    if (!giftMode || !teamId || teamStudents.length > 0) return
    setLoadingStudents(true)
    fetch(`/api/teaching_teams/${teamId}/students?per_page=100`, {
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        const students = (data.students || []).filter(s => s.user?.id !== currentUser?.id)
        setTeamStudents(students)
      })
      .catch(() => setTeamStudents([]))
      .finally(() => setLoadingStudents(false))
  }, [giftMode, teamId])

  const buttonLabel = !selectedPkg
    ? (translations?.selectPackageHint || '← 請先選擇方案')
    : isTrialSelected
      ? (selectedPkg.totalPrice === 0
          ? (translations?.freeTrialButton || 'Free Trial')
          : `${translations?.freeTrialBuy || 'Buy Trial'} ${formatCurrency(selectedPkg.totalPrice, selectedPkg.currency)}`)
      : (translations?.purchaseButton || 'Purchase Course')

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between sticky top-0 z-10"
          style={{ backgroundColor: '#E6F0FF' }}
        >
          <h2 className="text-lg font-semibold" style={{ color: '#3366FF' }}>
            {translations?.courseDetailTitle || '課程詳情'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleCopyClick(courseShareUrl)}
              className="cursor-pointer text-gray-400 hover:text-blue-500 transition-colors p-1 rounded-lg hover:bg-blue-50"
              title={translations?.shareCourseLink || '複製課程連結'}
            >
              {isCopied ? (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              )}
            </button>
            <button
              onClick={onClose}
              className="cursor-pointer text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Course Video / Image */}
        <VideoOrImage videoUrl={course.videoUrl} imageUrl={course.imageUrl} alt={course.name} />

        {/* Course Info */}
        <div className="p-6">
          <h3 className="text-xl font-bold mb-1" style={{ color: '#222' }}>{course.name}</h3>
          {course.targetAudience && (
            <p className="text-sm mb-3" style={{ color: '#717171' }}>{course.targetAudience}</p>
          )}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
              {course.durationMinutes} {translations?.min || 'min'}
            </span>
            {course.studentsCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {course.studentsCount} {translations?.studentsUnit || '位學生'}
              </span>
            )}
            {course.lessonsCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {course.lessonsCount} {translations?.lessonsCompleted || '堂已完成'}
              </span>
            )}
          </div>
          {course.description && (
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#555' }}>{course.description}</p>
          )}
        </div>

        {/* Consultation CTA — between course content and purchase */}
        {consultationEnabled && onConsult && (
          <div className="mx-6 mb-4 rounded-xl p-4" style={{ backgroundColor: '#E6F0FF' }}>
            <p className="text-sm text-gray-700 mb-2">{translations?.courseDetailConsultTitle || '想了解更多？'}</p>
            <button
              onClick={() => {
                onClose()
                onConsult({ source: 'course_detail', courseContext: course.name })
              }}
              className="px-4 py-2 rounded-lg text-white text-sm font-medium cursor-pointer transition-colors hover:opacity-90"
              style={{ backgroundColor: colors?.primary || '#3366FF' }}
            >
              {translations?.courseDetailConsultButton || '先跟老師聊聊'}
            </button>
          </div>
        )}

        {/* Purchase Flow — clicking a package directly triggers purchase (same as sidebar) */}
        {visiblePkgs.length > 0 && (
          <div className="px-6 pb-6">
            {/* Gift toggle — only visible to team members, above package selector */}
            {isTeamMember && (
              <div className="border-t pt-4 mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    className={`relative w-10 h-6 rounded-full transition-colors ${giftMode ? 'bg-blue-500' : 'bg-gray-300'}`}
                    onClick={() => { setGiftMode(!giftMode); setSelectedRecipientId(null) }}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${giftMode ? 'translate-x-4' : ''}`} />
                  </div>
                  <span className="text-sm text-gray-700">
                    {translations?.giftToStudent || '送給學生'}
                  </span>
                </label>

                {giftMode && (
                  <div className="mt-3">
                    {loadingStudents ? (
                      <p className="text-sm text-gray-400">{translations?.loadingStudents || '載入學生列表...'}</p>
                    ) : teamStudents.length === 0 ? (
                      <p className="text-sm text-gray-400">{translations?.noStudents || '目前沒有學生'}</p>
                    ) : (
                      <select
                        value={selectedRecipientId || ''}
                        onChange={(e) => setSelectedRecipientId(e.target.value ? Number(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">{translations?.selectStudent || '選擇學生'}</option>
                        {teamStudents.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.user?.name || s.user?.email || `Student #${s.id}`}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className={`${isTeamMember ? '' : 'border-t pt-4 '}mb-4`}>
              <p className="text-sm font-medium text-gray-700 mb-3">{translations?.selectPackage || '選擇方案'}</p>
            </div>

            <PackageSelector
              packages={visiblePkgs}
              courseDuration={course.durationMinutes}
              selectedId={selectedPkgId}
              onSelect={(pkgId) => {
                const pkg = visiblePkgs.find(p => p.id === pkgId)
                onSelectPackage(course.id, pkgId, course.name, pkg?.isTrial ? 'trial' : pkg?.lessonCount)
              }}
              onPurchase={(pkg) => {
                if (pkg.isTrial && pkg.totalPrice === 0) onClaimTrial?.(course)
                else if (giftMode && selectedRecipientId) {
                  onPurchase(course, pkg, { recipientTeamStudentId: selectedRecipientId })
                } else {
                  onPurchase(course, pkg)
                }
              }}
              trialUsed={trialUsed}
              colors={colors}
              translations={translations}
              discountPreviews={discountPreviews}
            />
          </div>
        )}
      </div>
      <PromotionLinkModal
        isOpen={modalState.isOpen}
        onClose={closePromoModal}
        baseUrl={modalState.baseUrl}
        teamId={teamId}
        inviteCode={inviteCode}
        colors={colors}
        translations={translations?.promotionModal}
      />
    </div>
  )
}
